import React, { useEffect, useState, useCallback } from "react";
import { db } from "../firebase";
import { collection, addDoc, query, where, getDocs } from "firebase/firestore";
import { msalInstance, msalBereit, msLoginRedirect } from "../msalClient";

const MS_SCOPES = ["User.Read", "Mail.ReadWrite"];
const MS_ACCOUNT_KEY = "posteingang_ms_account_id";
const AB_DATUM_KEY = "posteingang_ab_datum";
const STANDARD_AB_DATUM = "2026-06-27";

function htmlZuText(html) {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  // CSS/Skripte entfernen, die sonst als Text mitgelesen werden (z.B. <style><!-- ... --></style> in Outlook-Mails)
  div.querySelectorAll("style, script, head, meta, link").forEach((el) => el.remove());
  // Zeilenumbrüche an Block-Elementen einfügen, damit der Text lesbar bleibt
  div.querySelectorAll("br").forEach((el) => el.replaceWith("\n"));
  div.querySelectorAll("p, div, tr, li, h1, h2, h3").forEach((el) => el.append("\n"));
  const text = div.textContent || div.innerText || "";
  return text.replace(/\n{3,}/g, "\n\n").replace(/[ \t]+/g, " ").trim();
}

export default function Posteingang({ user }) {
  const [msToken, setMsToken] = useState(null);
  const [msAccount, setMsAccount] = useState(null);
  const [mails, setMails] = useState([]);
  const [offeneMailId, setOffeneMailId] = useState(null);
  const [volltext, setVolltext] = useState({});
  const [volltextHtml, setVolltextHtml] = useState({});
  const [ladeFehler, setLadeFehler] = useState("");
  const [laedt, setLaedt] = useState(false);
  const [verschiebenOffen, setVerschiebenOffen] = useState(null);
  const [ablageOffen, setAblageOffen] = useState(null);
  const [ablageKategorie, setAblageKategorie] = useState("beruflich");
  const [abDatum, setAbDatum] = useState(localStorage.getItem(AB_DATUM_KEY) || STANDARD_AB_DATUM);

  // ── Stille Anmeldung beim Laden versuchen ───────────────────────────────────
  useEffect(() => {
    (async () => {
      await msalBereit;
      const gespeicherteId = localStorage.getItem(MS_ACCOUNT_KEY);
      let konto = gespeicherteId ? msalInstance.getAccount({ homeAccountId: gespeicherteId }) : null;
      // Fallback: alle gecachten Konten durchsuchen
      if (!konto) {
        const alleKonten = msalInstance.getAllAccounts();
        if (alleKonten.length > 0) konto = alleKonten[0];
      }
      if (konto) {
        try {
          const ergebnis = await msalInstance.acquireTokenSilent({ scopes: MS_SCOPES, account: konto });
          setMsAccount(ergebnis.account);
          setMsToken(ergebnis.accessToken);
          localStorage.setItem(MS_ACCOUNT_KEY, ergebnis.account.homeAccountId);
        } catch (err) {
          console.warn("[MSAL Posteingang] Stille Anmeldung fehlgeschlagen:", err.message);
        }
      }
    })();
  }, []);

  const verbinden = async () => {
    try {
      await msLoginRedirect(MS_SCOPES, "posteingang");
    } catch (err) { setLadeFehler("Anmeldung fehlgeschlagen: " + err.message); }
  };

  const trennen = () => {
    localStorage.removeItem(MS_ACCOUNT_KEY);
    setMsAccount(null);
    setMsToken(null);
    setMails([]);
  };

  // ── Mails laden ───────────────────────────────────────────────────────────────
  const mailsLaden = useCallback(async () => {
    if (!msToken) return;
    setLaedt(true);
    setLadeFehler("");
    try {
      const filter = encodeURIComponent(`receivedDateTime ge ${abDatum}T00:00:00Z`);
      const url = `https://graph.microsoft.com/v1.0/me/mailFolders/inbox/messages?$filter=${filter}&$top=50&$orderby=receivedDateTime desc&$select=subject,from,bodyPreview,receivedDateTime,isRead`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${msToken}` } });
      const daten = await res.json();
      if (daten.error) { setLadeFehler(daten.error.message); setLaedt(false); return; }
      setMails(daten.value || []);
    } catch (err) { setLadeFehler(err.message); }
    setLaedt(false);
  }, [msToken, abDatum]);

  useEffect(() => { mailsLaden(); }, [mailsLaden]);

  const mailOeffnen = async (mail) => {
    if (offeneMailId === mail.id) { setOffeneMailId(null); return; }
    setOffeneMailId(mail.id);
    if (volltext[mail.id]) return;
    try {
      const url = `https://graph.microsoft.com/v1.0/me/messages/${mail.id}?$select=body`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${msToken}` } });
      const daten = await res.json();
      const rawHtml = daten.body?.contentType === "html" ? (daten.body.content || "") : "";
      const text = rawHtml ? htmlZuText(rawHtml) : (daten.body?.content || "");
      setVolltext((prev) => ({ ...prev, [mail.id]: text }));
      if (rawHtml) setVolltextHtml((prev) => ({ ...prev, [mail.id]: rawHtml }));
    } catch (err) { setLadeFehler(err.message); }
  };

  const mailArchivieren = async (mailId) => {
    try {
      await fetch(`https://graph.microsoft.com/v1.0/me/messages/${mailId}/move`, {
        method: "POST",
        headers: { Authorization: `Bearer ${msToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ destinationId: "archive" }),
      });
      setMails((prev) => prev.filter((m) => m.id !== mailId));
    } catch (err) { setLadeFehler(err.message); }
  };

  const mailText = (mail) => volltext[mail.id] || mail.bodyPreview || "";

  // ── In Notiz / Aufgabe / Projekt verschieben ────────────────────────────────
  const alsNotizSpeichern = async (mail) => {
    await addDoc(collection(db, "notizen"), {
      text: mail.subject || "(Ohne Betreff)",
      uid: user.uid,
      notiz: { text: mailText(mail) },
      erstellt: new Date(),
    });
    await mailArchivieren(mail.id);
  };

  const alsAufgabeSpeichern = async (mail) => {
    await addDoc(collection(db, "todo"), {
      titel: mail.subject || "(Ohne Betreff)",
      kategorie: "privat",
      punkte: [{ text: mailText(mail).slice(0, 300), erledigt: false, id: Date.now().toString() }],
      uid: user.uid,
      reihenfolge: Date.now(),
      erstellt: new Date(),
    });
    await mailArchivieren(mail.id);
  };

  const alsAblageSpeichern = async (mail, kategorie) => {
    await addDoc(collection(db, "mailablage"), {
      betreff: mail.subject || "(Ohne Betreff)",
      text: volltextHtml[mail.id] || mailText(mail),
      absender: mail.from?.emailAddress?.address || "",
      datum: mail.receivedDateTime || null,
      kategorie: kategorie || "beruflich",
      uid: user.uid,
      erstellt: new Date(),
    });
    await mailArchivieren(mail.id);
    setAblageOffen(null);
  };

  const alsProjektSpeichern = async (mail, projektId) => {
    const neuerUnterpunkt = {
      text: mail.subject || "(Ohne Betreff)",
      erledigt: false,
      id: Date.now().toString(),
      prioritaet: "mittel",
      erledigtBis: "",
      notiz: { text: mailText(mail) },
    };
    if (projektId === "__neu__") {
      await addDoc(collection(db, "projekte"), {
        titel: mail.subject || "(Ohne Betreff)",
        unterpunkte: [neuerUnterpunkt],
        uid: user.uid,
        reihenfolge: Date.now(),
        erstellt: new Date(),
      });
    } else {
      const snap = await getDocs(query(collection(db, "projekte"), where("uid", "==", user.uid)));
      const projektDoc = snap.docs.find((d) => d.id === projektId);
      if (projektDoc) {
        const { updateDoc, doc: docRef } = await import("firebase/firestore");
        const bisherige = projektDoc.data().unterpunkte || [];
        await updateDoc(docRef(db, "projekte", projektId), {
          unterpunkte: [...bisherige, neuerUnterpunkt],
        });
      }
    }
    await mailArchivieren(mail.id);
    setVerschiebenOffen(null);
  };

  const formatDatum = (iso) => new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

  // ── Nicht verbunden ───────────────────────────────────────────────────────────
  if (!msToken) {
    return (
      <div style={styles.seite}>
        <h2 style={styles.titel}>📧 Posteingang</h2>
        <div style={styles.verbindenBox}>
          <p>Verbinde dein dediziertes Postfach (z.B. das App-Outlook-Konto), um Mails hier zu sehen und in Notizen, Aufgaben oder Projekte zu verschieben.</p>
          <button style={styles.verbindenBtn} onClick={verbinden}>Mit Outlook-Postfach verbinden</button>
          {ladeFehler && <p style={styles.fehler}>{ladeFehler}</p>}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.seite}>
      <div style={styles.kopfzeile}>
        <h2 style={styles.titel}>📧 Posteingang {msAccount && <span style={styles.kontoLabel}>({msAccount.username})</span>}</h2>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          <label style={styles.datumLabel}>
            Anzeigen ab
            <input
              type="date"
              style={styles.datumInput}
              value={abDatum}
              onChange={(e) => { setAbDatum(e.target.value); localStorage.setItem(AB_DATUM_KEY, e.target.value); }}
            />
          </label>
          <button style={styles.trennenBtn} onClick={trennen}>🔌 Trennen</button>
          <button style={styles.aktualisierenBtn} onClick={mailsLaden}>🔄 Aktualisieren</button>
        </div>
      </div>

      {ladeFehler && <p style={styles.fehler}>{ladeFehler}</p>}
      {laedt && <p style={styles.hinweis}>Lädt...</p>}
      {!laedt && mails.length === 0 && <p style={styles.leer}>Keine Mails im Posteingang.</p>}

      <div style={styles.liste}>
        {mails.map((mail) => (
          <div key={mail.id} style={styles.mailKarte}>
            <div style={styles.mailKopf} onClick={() => mailOeffnen(mail)}>
              <div style={{ flex: 1 }}>
                <div style={styles.absender}>{mail.from?.emailAddress?.name || mail.from?.emailAddress?.address || "Unbekannt"}</div>
                <div style={styles.betreff}>{mail.subject || "(Ohne Betreff)"}</div>
                {offeneMailId !== mail.id && <div style={styles.vorschau}>{mail.bodyPreview}</div>}
              </div>
              <div style={styles.datum}>{formatDatum(mail.receivedDateTime)}</div>
            </div>

            {offeneMailId === mail.id && (
              <div style={styles.mailDetail}>
                <div style={styles.aktionen}>
                  <button style={styles.aktionBtn} onClick={() => alsNotizSpeichern(mail)}>📝 Notiz</button>
                  <button style={styles.aktionBtn} onClick={() => alsAufgabeSpeichern(mail)}>✅ Aufgabe</button>
                  <button style={styles.aktionBtn} onClick={() => setVerschiebenOffen(verschiebenOffen === mail.id ? null : mail.id)}>📁 Projekt</button>
                  <button style={{ ...styles.aktionBtn, backgroundColor: ablageOffen === mail.id ? "#e8f0fe" : undefined }} onClick={() => setAblageOffen(ablageOffen === mail.id ? null : mail.id)}>📬 Ablage</button>
                  <button style={{ ...styles.aktionBtn, backgroundColor: "#fce8e6", color: "#c62828" }} onClick={() => mailArchivieren(mail.id)}>🗑️ Archivieren</button>
                </div>
                {verschiebenOffen === mail.id && (
                  <ProjektAuswahl user={user} onWaehlen={(projektId) => alsProjektSpeichern(mail, projektId)} />
                )}
                {ablageOffen === mail.id && (
                  <div style={styles.projektAuswahl}>
                    <select style={styles.select} value={ablageKategorie} onChange={(e) => setAblageKategorie(e.target.value)}>
                      <option value="beruflich">💼 Beruflich</option>
                      <option value="privat">🏠 Privat</option>
                    </select>
                    <button style={{ ...styles.aktionBtn, backgroundColor: "#1a73e8", color: "white", marginLeft: "0.5rem" }} onClick={() => alsAblageSpeichern(mail, ablageKategorie)}>Ablegen</button>
                  </div>
                )}
                <p style={styles.volltext}>{mailText(mail)}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ProjektAuswahl({ user, onWaehlen }) {
  const [projekte, setProjekte] = useState([]);
  useEffect(() => {
    (async () => {
      const snap = await getDocs(query(collection(db, "projekte"), where("uid", "==", user.uid)));
      setProjekte(snap.docs.map((d) => ({ id: d.id, titel: d.data().titel })));
    })();
  }, [user.uid]);

  return (
    <div style={styles.projektAuswahl}>
      <select style={styles.select} onChange={(e) => e.target.value && onWaehlen(e.target.value)} defaultValue="">
        <option value="" disabled>Projekt wählen...</option>
        <option value="__neu__">+ Neues Projekt erstellen</option>
        {projekte.map((p) => <option key={p.id} value={p.id}>{p.titel}</option>)}
      </select>
    </div>
  );
}

const styles = {
  seite: { padding: "clamp(0.75rem, 2.5vw, 2rem)", width: "100%", boxSizing: "border-box" },
  kopfzeile: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" },
  titel: { margin: 0, color: "#1a3558" },
  kontoLabel: { fontSize: "0.75rem", color: "#888", fontWeight: "400" },
  verbindenBox: { backgroundColor: "white", borderRadius: "8px", padding: "2rem", textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" },
  verbindenBtn: { padding: "0.7rem 1.5rem", backgroundColor: "#0078d4", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "1rem", marginTop: "0.5rem" },
  trennenBtn: { padding: "0.5rem 0.8rem", backgroundColor: "#f0f2f5", border: "1px solid #ddd", borderRadius: "6px", cursor: "pointer", fontSize: "0.85rem" },
  aktualisierenBtn: { padding: "0.5rem 0.8rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.85rem" },
  datumLabel: { display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.82rem", color: "#555" },
  datumInput: { padding: "0.35rem 0.5rem", border: "1px solid #ddd", borderRadius: "6px", fontSize: "0.85rem" },
  fehler: { color: "#c62828", fontSize: "0.85rem" },
  hinweis: { color: "#777", fontSize: "0.9rem" },
  leer: { color: "#999", textAlign: "center", marginTop: "2rem" },
  liste: { display: "flex", flexDirection: "column", gap: "0.5rem" },
  mailKarte: { backgroundColor: "white", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" },
  mailKopf: { display: "flex", gap: "0.8rem", padding: "0.8rem 1rem", cursor: "pointer", alignItems: "flex-start" },
  absender: { fontSize: "0.78rem", color: "#888" },
  betreff: { fontWeight: "600", color: "#1a3558", fontSize: "0.95rem", margin: "0.1rem 0" },
  vorschau: { fontSize: "0.82rem", color: "#777", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  datum: { fontSize: "0.75rem", color: "#999", whiteSpace: "nowrap" },
  mailDetail: { padding: "0 1rem 1rem", borderTop: "1px solid #f0f0f0" },
  volltext: { fontSize: "0.88rem", color: "#333", whiteSpace: "pre-wrap", lineHeight: 1.5, marginTop: "0.7rem" },
  aktionen: { display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.7rem" },
  aktionBtn: { padding: "0.4rem 0.8rem", backgroundColor: "#f0f2f5", border: "1px solid #ddd", borderRadius: "6px", cursor: "pointer", fontSize: "0.82rem" },
  projektAuswahl: { marginTop: "0.6rem" },
  select: { padding: "0.5rem", border: "1px solid #ddd", borderRadius: "6px", fontSize: "0.88rem", width: "100%" },
};
